import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import { FileDropZone } from "@/components/common/FileDropZone";
import { ExtractProfileFromDocumentDocument } from "@/graphql/generated/operations";
import { hasExtractedProposals, type CvImportPreview } from "@/components/save/cv-extraction-to-preview";
import { ingestFiles } from "@/lib/file-ingest";
import { MAX_DOCUMENT_UPLOAD_BYTES } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { useCvImportStore } from "@/stores/cv-import-store";

// CV Extractor: upload one document, call the non-persisting
// `extractProfileFromDocument` (genesis #83), then hand the extraction to the
// profile page, which opens the SAME in-line guided merge review the LinkedIn
// import uses. Nothing is written here — the merge review commits on the profile
// page.

const ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.html,.png,.jpg,.jpeg";

// The analysis is an LLM document extraction, so it can be slow — but it must not
// hang forever. If it exceeds this, we abort the in-flight request and surface a
// retryable error rather than leaving the user stuck on a spinner with no way out.
const EXTRACT_TIMEOUT_MS = 120_000;
const ELAPSED_TICK_MS = 1_000;
const ABORT_TIP_AFTER_S = 20;

// m:ss — extraction is bounded by EXTRACT_TIMEOUT_MS (2 min), so no hour segment.
function formatElapsed(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

interface ExtractResult {
  merge: CvImportPreview;
  fileName: string;
}

export function ProfileExtractionDialog({ onClose }: { onClose: () => void }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const stageCvImport = useCvImportStore((s) => s.start);
  const [extract] = useMutation(ExtractProfileFromDocumentDocument);

  const [busy, setBusy] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExtractResult | null>(null);
  // Set while an extraction is in flight so the user can abort it (Cancel while
  // analysing) and so a timeout can cancel the request instead of hanging.
  const abortRef = useRef<AbortController | null>(null);

  const onFiles = async (files: FileList) => {
    setError(null);
    const { accepted, rejected } = await ingestFiles(files, {
      maxBytes: MAX_DOCUMENT_UPLOAD_BYTES,
      accept: ACCEPT,
    });
    if (rejected.length > 0) {
      const reason = rejected[0]!.reason;
      setError(
        reason === "tooLarge"
          ? intl.formatMessage({ id: "profileExtraction.fileTooLarge", defaultMessage: "File is too large (max. 10 MB)." })
          : intl.formatMessage({ id: "profileExtraction.unsupportedType", defaultMessage: "Unsupported file type." }),
      );
      return;
    }
    const file = accepted[0];
    if (!file) return;

    setElapsedSeconds(0);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, EXTRACT_TIMEOUT_MS);
    try {
      const res = await extract({
        variables: { file: file.content, fileName: file.name },
        context: { fetchOptions: { signal: controller.signal } },
      });
      const merge = res.data?.extractProfileFromDocument;
      if (!merge) {
        throw new Error(
          intl.formatMessage({ id: "profileExtraction.noResult", defaultMessage: "The document could not be analysed." }),
        );
      }
      setResult({ merge, fileName: file.name });
    } catch (e) {
      if (timedOut) {
        setError(
          intl.formatMessage({
            id: "profileExtraction.timeout",
            defaultMessage: "Analysis is taking too long. Please try again.",
          }),
        );
      } else if (controller.signal.aborted) {
        return; // user cancelled — the dialog is closing, so show nothing
      } else {
        setError(userErrorMessage(intl, e));
      }
    } finally {
      clearTimeout(timer);
      abortRef.current = null;
      // Keep the spinner only on a user abort (the dialog is closing); success,
      // error and timeout all return to an interactive state.
      if (!controller.signal.aborted || timedOut) setBusy(false);
    }
  };

  // Elapsed extraction counter. Compute from a monotonic start timestamp so a
  // throttled tab or system clock change does not drift the display.
  useEffect(() => {
    if (!busy) return;
    const startedAt = performance.now();
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((performance.now() - startedAt) / 1000));
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [busy]);

  // Abort/close escape hatch: while analysing, the footer shows the same
  // bottom-left abort affordance as the LinkedIn importer. Aborting actually
  // cancels the request because this is a client mutation.
  const cancelOrClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  const onReview = () => {
    // Defensive: the Review button only renders inside the `result != null`
    // branch, so this never fires with a null result — guard kept, coverage-ignored.
    /* v8 ignore next */
    if (!result) return;
    // Stash the raw extraction; the profile page builds the two-sided merge
    // preview (it has the loaded profile + reference lookups) and opens the
    // in-line guided review — the same one the LinkedIn import uses.
    stageCvImport(result.merge, result.fileName);
    onClose();
    navigate("/save/basics");
  };

  const hasProposals = result != null && hasExtractedProposals(result.merge);

  return (
    <div
      id="extraction"
      className="fixed inset-0 z-[1000] flex bg-ink/30 p-4 backdrop-blur-[1px]"
      data-graphql="ExtractProfileFromDocument"
    >
      <div className="m-auto flex max-h-full w-full max-w-[520px] flex-col overflow-hidden rounded-md bg-page shadow-xl ring-1 ring-ink/10">
        <div className="flex items-center justify-between gap-3 border-b border-ink/10 px-5 py-3">
          <div className="min-w-0">
            <div className="smycles-field-label text-ink-muted">
              <FormattedMessage id="save.extraction.poweredBy" defaultMessage="Powered by smycles AI" />
            </div>
            <div className="font-serif text-smycles-display-s leading-tight">
              <FormattedMessage id="profileExtraction.title" defaultMessage="CV Extractor" />
            </div>
          </div>
          {!busy ? (
            <Button id="extraction-close" variant="secondary" onClick={cancelOrClose} className="px-3 py-1 text-smycles-micro">
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {busy ? (
            <div className="py-4">
              <div role="status" aria-live="polite">
                <p className="smycles-field-meta leading-snug text-ink">
                  <FormattedMessage
                    id="profileExtraction.analysing"
                    defaultMessage="Extracting profile data from your document - this can take up to two minutes..."
                  />
                </p>
              </div>
              {/* Deliberately aria-hidden and outside live regions so the
                  per-second tick is not announced on every change. */}
              <p className="smycles-field-meta mt-2 tabular-nums text-ink-muted" aria-hidden="true">
                <FormattedMessage
                  id="profileExtraction.elapsed"
                  defaultMessage="Elapsed time: {time}"
                  values={{ time: formatElapsed(elapsedSeconds) }}
                />
              </p>
              {/* Mounted empty from the start; the text toggles in after 20s so
                  screen readers announce the delayed abort guidance. */}
              <p role="status" aria-live="polite" className="smycles-field-meta mt-2 leading-snug text-ink">
                {elapsedSeconds >= ABORT_TIP_AFTER_S && (
                  <FormattedMessage
                    id="profileExtraction.abortTip"
                    defaultMessage="Still extracting - you can keep waiting, or abort and try again with the same document. Nothing has been saved yet."
                  />
                )}
              </p>
            </div>
          ) : result ? (
            hasProposals ? (
              <div className="space-y-4">
                <div className="text-smycles-meta text-ink">
                  <FormattedMessage
                    id="profileExtraction.ready"
                    defaultMessage="We found details to review in {file}."
                    values={{ file: result.fileName }}
                  />
                </div>
                <div className="flex justify-end">
                  <Button id="extraction-review" variant="primary" onClick={onReview} className="px-4 py-1.5 text-smycles-label">
                    <FormattedMessage id="profileExtraction.review" defaultMessage="Review changes" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div id="extraction-nothing-new" className="text-smycles-meta text-ink">
                  <FormattedMessage
                    id="profileExtraction.nothingNew"
                    defaultMessage="Nothing new found – your profile already covers this document."
                  />
                </div>
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={onClose} className="px-4 py-1.5 text-smycles-label">
                    <FormattedMessage id="common.close" defaultMessage="Close" />
                  </Button>
                </div>
              </div>
            )
          ) : (
            <>
              <FileDropZone
                inputId="extraction-file"
                variant="zone"
                clickToOpen
                multiple={false}
                accept={ACCEPT}
                onFiles={(fl) => void onFiles(fl)}
                label={
                  <FormattedMessage
                    id="profileExtraction.dropZone"
                    defaultMessage="Drag a document here or click to upload"
                  />
                }
                hint={
                  <FormattedMessage
                    id="profileExtraction.dropZoneHint"
                    defaultMessage="CV, LinkedIn export, application form … · PDF, DOCX, image · max. 10 MB"
                  />
                }
              />
              {error ? <div id="extraction-error" className="text-smycles-meta text-red-600">{error}</div> : null}
            </>
          )}
        </div>
        {busy ? (
          <div className="flex justify-end gap-2 border-t border-ink/5 px-5 py-4">
            <Button variant="secondary" onClick={cancelOrClose} className="mr-auto">
              <FormattedMessage id="profileExtraction.abort" defaultMessage="Abort extraction" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
